import { useCallback, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useOfflineQueue } from '../offline';

export const useRefetchOnFocus = (refetch, { enabled = true, skipInitialFocus = true } = {}) => {
	const { isReady, isOnline } = useOfflineQueue();
	const shouldRefetch = Boolean(enabled && isReady && isOnline && typeof refetch === 'function');
	const hasFocusedRef = useRef(false);

	// Read changing values through refs so the focus effect only fires on real
	// blur -> focus transitions. With them as effect dependencies, mutation
	// loading flags toggling `enabled` re-ran the effect mid-screen and
	// refetched (full-screen spinner) after every edit, e.g. each picklist tick.
	const shouldRefetchRef = useRef(shouldRefetch);
	shouldRefetchRef.current = shouldRefetch;
	const refetchRef = useRef(refetch);
	refetchRef.current = refetch;
	const skipInitialFocusRef = useRef(skipInitialFocus);
	skipInitialFocusRef.current = skipInitialFocus;

	useFocusEffect(useCallback(() => {
		if (!hasFocusedRef.current) {
			hasFocusedRef.current = true;
			if (skipInitialFocusRef.current) {
				return undefined;
			}
		}

		if (!shouldRefetchRef.current) {
			return undefined;
		}

		let isActive = true;
		Promise.resolve(refetchRef.current()).catch(error => {
			if (isActive) {
				console.log('Error refreshing screen on focus', error);
			}
		});

		return () => {
			isActive = false;
		};
	}, []));
};
